import { useState, useEffect } from 'react';
import api from '../../services/api';
import toast from 'react-hot-toast';
import { 
  DollarSign, 
  Search, 
  Filter, 
  CreditCard, 
  FileCheck, 
  ArrowRight,
  ExternalLink,
  CheckCircle2,
  Clock,
  FileDown
} from 'lucide-react';

const AccountantPayoutDashboard = () => {
  const [surgeries, setSurgeries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processingId, setProcessingId] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [selectedSurgery, setSelectedSurgery] = useState(null);
  
  // Form State
  const [formData, setFormData] = useState({
    amount: '',
    transactionId: '',
    payoutType: 'both',
    doctorShare: '50',
    hospitalShare: '50',
    description: '',
    documentUrl: 'https://cdn-icons-png.flaticon.com/512/3371/3371191.png' // Mock PDF URL
  });

  useEffect(() => {
    fetchCompletedSurgeries();
  }, []);

  const fetchCompletedSurgeries = async () => {
    try {
      setLoading(true);
      // Fetching surgeries with status 'surgery_completed' that need payout
      const response = await api.get('/surgeries?status=surgery_completed');
      setSurgeries(response.data.data.surgeries || []);
    } catch (error) {
      toast.error('Failed to load pending payouts');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenPayout = (surgery) => {
    setSelectedSurgery(surgery);
    setFormData({
        ...formData,
        amount: surgery.surgeryCost || ''
    });
    setShowModal(true);
  };

  const handleSubmitPayout = async (e) => {
    e.preventDefault();
    setProcessingId(selectedSurgery._id);
    
    try {
      // ATOMIC ACTION: Process and Complete in one shot
      await api.post('/payouts/process-atomic', {
        surgeryId: selectedSurgery._id,
        amount: formData.amount,
        payoutType: formData.payoutType,
        transactionId: formData.transactionId,
        documentUrl: formData.documentUrl,
        notes: formData.description || `Payout finalized for ${selectedSurgery.bookingId}`
      });

      toast.success('Payout Dispatched & Booking Closed Successfully!');
      setShowModal(false);
      fetchCompletedSurgeries();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Payout processing failed');
    } finally {
      setProcessingId(null);
    }
  };

  const handleExportReport = async () => {
    try {
      toast.loading('Generating Payout Export...');
      const response = await api.get('/payouts/export', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Doxez_Payout_Audit_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      toast.dismiss();
      toast.success('Audit Ledger Downloaded');
    } catch (error) {
      toast.dismiss();
      toast.error('Export failed');
    }
  };

  return (
    <div className="p-6 space-y-8 bg-gray-50 min-h-screen">
      
      {/* Header Section */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
           <div className="flex items-center gap-2 mb-1">
             <DollarSign className="w-5 h-5 text-emerald-600"/>
             <span className="text-sm font-bold text-emerald-600 uppercase tracking-widest">Financial Ops</span>
           </div>
           <h1 className="text-3xl font-black text-[#143E78]">Payout Action Center</h1>
           <p className="text-gray-500 font-medium">Verify completed surgeries and dispatch final payouts to stakeholders.</p>
        </div>
        
        <div className="flex items-center gap-4 bg-white p-2 rounded-2xl shadow-sm border border-gray-100">
           <div className="flex flex-col items-end px-4">
              <span className="text-[10px] font-bold text-gray-400 uppercase">Awaiting Action</span>
              <span className="text-xl font-black text-orange-500">{surgeries.length}</span>
           </div>
           <button 
              onClick={handleExportReport}
              className="flex items-center gap-2 px-4 py-2 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 rounded-xl text-xs font-black transition-all border border-emerald-100"
           >
              <FileDown size={14} /> Export Audit
           </button>
           <div className="w-[1px] h-10 bg-gray-100"></div>
           <div className="bg-orange-50 p-3 rounded-xl">
             <Clock className="w-6 h-6 text-orange-500" />
           </div>
        </div>
      </div>

      {/* Main Table Container */}
      <div className="bg-white rounded-3xl shadow-xl shadow-gray-200/50 border border-gray-100 overflow-hidden">
        <div className="p-6 border-b border-gray-50 flex items-center justify-between bg-gradient-to-r from-gray-50 to-white">
           <div className="relative w-72">
             <Search className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
             <input 
                placeholder="Search Booking ID..." 
                className="w-full pl-10 pr-4 py-2 bg-white border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-[#143E78] transition-all"
             />
           </div>
           <button className="flex items-center gap-2 px-4 py-2 text-sm font-bold text-gray-600 hover:bg-gray-100 rounded-xl transition-colors">
              <Filter className="w-4 h-4" /> Filter By Date
           </button>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-gray-50/50">
                <th className="px-6 py-4 text-[11px] font-black text-gray-400 uppercase">Booking ID</th>
                <th className="px-6 py-4 text-[11px] font-black text-gray-400 uppercase">Patient & Treatment</th>
                <th className="px-6 py-4 text-[11px] font-black text-gray-400 uppercase">Hospital & Doctor</th>
                <th className="px-6 py-4 text-[11px] font-black text-gray-400 uppercase">Amount</th>
                <th className="px-6 py-4 text-[11px] font-black text-gray-400 uppercase text-center">Status</th>
                <th className="px-6 py-4 text-[11px] font-black text-gray-400 uppercase text-right">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {loading ? (
                <tr><td colSpan="6" className="py-20 text-center font-bold text-gray-400 animate-pulse">Scanning Financial Pipeline...</td></tr>
              ) : surgeries.length === 0 ? (
                <tr><td colSpan="6" className="py-20 text-center font-bold text-gray-400">All payouts cleared. Good job!</td></tr>
              ) : surgeries.map(surgery => (
                <tr key={surgery._id} className="group hover:bg-blue-50/30 transition-colors">
                  <td className="px-6 py-5">
                    <span className="font-mono font-bold text-[#143E78] bg-blue-50 px-2 py-1 rounded shadow-sm">
                      {surgery.bookingId}
                    </span>
                  </td>
                  <td className="px-6 py-5">
                    <div className="font-black text-gray-800">{surgery.lead?.patientName}</div>
                    <div className="text-xs text-gray-500 font-medium">{surgery.surgeryName}</div>
                  </td>
                  <td className="px-6 py-5">
                    <div className="font-bold text-gray-700">{surgery.hospital?.hospitalName}</div>
                    <div className="text-xs text-gray-400 flex items-center gap-1 italic">
                       Operated by Dr. {surgery.doctor?.user?.firstName || '---'}
                    </div>
                  </td>
                  <td className="px-6 py-5">
                    <div className="font-black text-emerald-600">₹{surgery.surgeryCost?.toLocaleString()}</div>
                  </td>
                  <td className="px-6 py-5 text-center">
                    <span className="bg-emerald-100 text-emerald-700 px-3 py-1 rounded-full text-[10px] font-black uppercase flex items-center justify-center gap-1 mx-auto w-max">
                       <CheckCircle2 className="w-3 h-3" /> Completed
                    </span>
                  </td>
                  <td className="px-6 py-5 text-right">
                    <button 
                       onClick={() => handleOpenPayout(surgery)}
                       className="px-4 py-2 bg-[#143E78] hover:bg-[#0f2c56] text-white text-xs font-black rounded-xl shadow-lg shadow-blue-900/20 flex items-center gap-2 ml-auto group-hover:scale-105 transition-all"
                    >
                      Process Payout <ArrowRight className="w-3 h-3" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Payout Processing Modal */}
      {showModal && selectedSurgery && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
           <div className="bg-white rounded-[32px] w-full max-w-2xl shadow-2xl overflow-hidden animate-in fade-in zoom-in duration-200">
              
              <div className="p-8 bg-gradient-to-br from-[#143E78] to-[#0f2c56] text-white relative">
                 <button onClick={() => setShowModal(false)} className="absolute top-6 right-6 text-white/50 hover:text-white transition-colors">✕</button>
                 <div className="flex items-center gap-3 mb-4">
                    <div className="bg-white/20 p-2 rounded-lg backdrop-blur-md"><CreditCard className="w-6 h-6 text-white" /></div>
                    <span className="text-sm font-bold uppercase tracking-widest text-blue-200">Payment Disbursement</span>
                 </div>
                 <h2 className="text-2xl font-black italic underline decoration-blue-500/50">Final Financial Settlement</h2>
                 <p className="text-blue-100 text-sm mt-1">Reviewing Booking: <b>{selectedSurgery.bookingId}</b> for {selectedSurgery.lead?.patientName}</p>
              </div>

              <form onSubmit={handleSubmitPayout} className="p-8 space-y-6">
                 
                 <div className="grid grid-cols-2 gap-6">
                    <div className="space-y-2">
                       <label className="text-xs font-black text-gray-400 uppercase tracking-wider">Final Approved Amount (₹)</label>
                       <input 
                         required
                         type="number"
                         value={formData.amount}
                         onChange={(e) => setFormData({...formData, amount: e.target.value})}
                         className="w-full px-4 py-3 bg-gray-50 border-2 border-gray-100 rounded-xl font-black text-xl text-[#143E78] focus:border-[#143E78] focus:bg-white outline-none transition-all"
                       />
                    </div>
                    <div className="space-y-2">
                       <label className="text-xs font-black text-gray-400 uppercase tracking-wider">Internal Transaction ID</label>
                       <input 
                         required
                         placeholder="TXN_992..."
                         value={formData.transactionId}
                         onChange={(e) => setFormData({...formData, transactionId: e.target.value})}
                         className="w-full px-4 py-3 bg-gray-50 border-2 border-gray-100 rounded-xl font-bold focus:border-[#143E78] focus:bg-white outline-none transition-all"
                       />
                    </div>
                 </div>

                 <div className="space-y-3">
                    <label className="text-xs font-black text-gray-400 uppercase tracking-wider">Payout Confirmation Attachment</label>
                    <div className="flex items-center gap-4 p-4 border-2 border-dashed border-gray-100 rounded-2xl bg-gray-50/50">
                       <div className="p-3 bg-white rounded-xl shadow-sm"><FileCheck className="w-8 h-8 text-[#143E78]" /></div>
                       <div className="flex-1">
                          <p className="text-xs font-bold text-gray-700">payout_receipt_{selectedSurgery.bookingId}.pdf</p>
                          <p className="text-[10px] text-gray-400">Ready to sync with cloud ledger</p>
                       </div>
                       <a href={formData.documentUrl} target="_blank" rel="noreferrer" className="p-2 hover:bg-gray-200 rounded-lg transition-colors"><ExternalLink className="w-4 h-4 text-gray-400" /></a>
                    </div>
                 </div>

                 <div className="pt-6 border-t border-gray-50 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                       <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse"></div>
                       <span className="text-[10px] font-black text-gray-400 uppercase">Logic: Verify ➔ Dispatch ➔ Close</span>
                    </div>
                    <button 
                       type="submit"
                       disabled={processingId !== null}
                       className="px-10 py-4 bg-[#143E78] hover:bg-[#0f2c56] text-white font-black rounded-2xl shadow-xl shadow-blue-900/30 disabled:opacity-50 flex items-center gap-3 transition-transform active:scale-95"
                    >
                       {processingId === selectedSurgery._id ? 'Finalizing Ledger...' : 'Confirm Payout & Close Deal'}
                    </button>
                 </div>

              </form>
           </div>
        </div>
      )}

    </div>
  );
};

export default AccountantPayoutDashboard;
